/**
 * Component: ConfigurationPage
 * SpecReferenceID: CONFIG-V1
 * Requirements: CONFIG-V1-R15, CONFIG-V1-R17, ADMIN-V1-R14, SAFETY-V1-R05
 *
 * Admin page for managing portal configuration.
 * Restructured with tab navigation: Global, Galen Chat, Galen Search, Search Settings, Safety.
 *
 * CONFIG-V1-R15: Admin interface for branding configuration management.
 * CONFIG-V1-R17: Admin interface for runtime management of non-sensitive AI service parameters.
 * ADMIN-V1-R14: System shall enable authorized administrators to toggle feature flags through a secure interface.
 * SAFETY-V1-R05: Read-only display of configurable, versioned, and auditable safety rules.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Loader2, AlertCircle, Save, RotateCcw, Palette, Image, FileText, Search, Plus, Trash2, Activity, Cpu, Settings, ToggleRight, Shield, ChevronDown, ChevronRight } from 'lucide-react';

import api, { getErrorMessage } from '@/lib/api';
import { useBranding, useFeatureFlags } from '@/contexts';
import type { BrandingColors, BrandingDisclaimers, BrandingConfigApiResponse, CitationDisplayMode, ClassificationTaxonomy, ColorMapping } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

/**
 * Color field configuration for rendering color inputs
 */
interface ColorField {
  key: keyof BrandingColors;
  label: string;
}

const COLOR_FIELDS: ColorField[] = [
  { key: 'background', label: 'Background' },
  { key: 'foreground', label: 'Foreground' },
  { key: 'primary', label: 'Primary' },
  { key: 'primaryForeground', label: 'Primary Foreground' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'secondaryForeground', label: 'Secondary Foreground' },
  { key: 'accent', label: 'Accent' },
  { key: 'accentForeground', label: 'Accent Foreground' },
];

/**
 * Parse an HSL string to a CSS color for the preview swatch.
 * Accepts HSL values like "222.2 47.4% 11.2%" (without hsl() wrapper).
 */
function hslToPreviewColor(hsl: string | null | undefined): string | undefined {
  if (!hsl) return undefined;
  return `hsl(${hsl})`;
}

/**
 * ADMIN-V1-R14: Feature flag info from admin endpoint
 */
interface FeatureFlagInfo {
  name: string;
  enabled: boolean;
  description: string;
  dependencies: string[];
  dependents: string[];
}

/**
 * SAFETY-V1-R05: Safety rule from admin safety config endpoint
 */
interface SafetyRuleInfo {
  rule_id: string;
  action: string;
  category: string;
  message: string;
  pattern_count: number;
  patterns: string[];
  effective_status: string;
  effective_status_reason: string | null;
}

/**
 * SAFETY-V1-R05: Safety config response from admin endpoint
 */
interface SafetyConfigData {
  version: string;
  last_updated: string | null;
  rules: SafetyRuleInfo[];
  allowlist_context: string[];
  rule_count: number;
  timestamp: string;
}

export function ConfigurationPage(): React.ReactElement {
  const { branding, refetch, hasLoadedOnce: brandingLoaded } = useBranding();
  const { refetch: featureFlagRefetch } = useFeatureFlags();

  // Form state — initialized from current branding
  const [appName, setAppName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoDarkUrl, setLogoDarkUrl] = useState('');
  const [logoAltText, setLogoAltText] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [lightColors, setLightColors] = useState<BrandingColors>({});
  const [darkColors, setDarkColors] = useState<BrandingColors>({});
  const [disclaimers, setDisclaimers] = useState<BrandingDisclaimers>({});
  const [citationDisplayMode, setCitationDisplayMode] = useState<CitationDisplayMode>('inline');
  // CONFIG-V1-R17: Chat prompts state
  const [chatSystemPrompt, setChatSystemPrompt] = useState('');
  const [chatUngroundedFallbackMessage, setChatUngroundedFallbackMessage] = useState('');
  // CONFIG-V1-R17: Galen Search prompts state
  const [galenSearchSystemPrompt, setGalenSearchSystemPrompt] = useState('');
  const [galenSearchUngroundedFallbackMessage, setGalenSearchUngroundedFallbackMessage] = useState('');
  // CONFIG-V1-R17 (Bug #3888): Chat-specific LLM settings state
  const [chatTemperature, setChatTemperature] = useState<string>('');
  const [chatMaxTokens, setChatMaxTokens] = useState<string>('');
  const [chatTopP, setChatTopP] = useState<string>('');
  const [chatGroundingTopN, setChatGroundingTopN] = useState<string>('');
  const [chatSemanticConfiguration, setChatSemanticConfiguration] = useState('');
  const [chatScoringProfile, setChatScoringProfile] = useState('');
  const [chatPastMessagesIncluded, setChatPastMessagesIncluded] = useState<string>('');
  // CONFIG-V1-R17 (Bug #3889): Search-specific LLM settings state
  const [galenSearchTemperature, setGalenSearchTemperature] = useState<string>('');
  const [galenSearchMaxTokens, setGalenSearchMaxTokens] = useState<string>('');
  const [galenSearchTopP, setGalenSearchTopP] = useState<string>('');
  const [galenSearchGroundingTopN, setGalenSearchGroundingTopN] = useState<string>('');
  const [searchHybridTopN, setSearchHybridTopN] = useState<string>('');
  const [galenSearchSemanticConfiguration, setGalenSearchSemanticConfiguration] = useState('');
  const [galenSearchScoringProfile, setGalenSearchScoringProfile] = useState('');
  const [galenSearchPastMessagesIncluded, setGalenSearchPastMessagesIncluded] = useState<string>('');
  // CONFIG-V1-R17 (Bug #3890): Search page settings state
  const [searchSemanticTopN, setSearchSemanticTopN] = useState<string>('');
  const [searchSimpleTopN, setSearchSimpleTopN] = useState<string>('');
  const [searchDefaultMode, setSearchDefaultMode] = useState<string>('');
  // CONFIG-V1-R17 (Bug #3979): Per-page search mode settings
  const [semanticSearchDefaultMode, setSemanticSearchDefaultMode] = useState<string>('');
  const [galenSearchDefaultMode, setGalenSearchDefaultMode] = useState<string>('');
  const [searchSemanticConfiguration, setSearchSemanticConfiguration] = useState('');
  // SEARCH-SEMANTIC-V1-R02 (Bug #3893): Vector search settings state
  const [searchVectorK, setSearchVectorK] = useState<string>('');
  const [searchVectorExhaustive, setSearchVectorExhaustive] = useState(false);
  const [queryEmbeddingCacheTtlMinutes, setQueryEmbeddingCacheTtlMinutes] = useState<string>('');
  const [searchVectorProfile, setSearchVectorProfile] = useState('');
  // SEARCH-SEMANTIC-V1-R03: COR/LOE colour mapping state
  const [corColorMappings, setCorColorMappings] = useState<ColorMapping[]>([]);
  const [loeColorMappings, setLoeColorMappings] = useState<ColorMapping[]>([]);
  // CONFIG-V1-R11 (Bug #4306): chat_use_grounding admin toggle. Three-state
  // select — 'default' sends null (fall back to env var), 'enabled' sends
  // true, 'disabled' sends false. Treating the absent case as a distinct
  // option rather than conflating it with `false` preserves the three-tier
  // branding -> Settings -> default resolution that CONFIG-V1-R17 shipped.
  const [chatUseGrounding, setChatUseGrounding] = useState<
    'default' | 'enabled' | 'disabled'
  >('default');
  // REPORTING-V1-R05 (Bug #4307): classification prompt + taxonomy admin
  // state. The prompt is a plain textarea (empty -> null -> use default).
  // The taxonomy tri-state is "use default" vs "custom", because a partially
  // populated taxonomy would fail Pydantic's min_length=1 per-list rule and
  // the user should see a clear switch rather than being surprised by a
  // save-time validation error.
  const [reportingClassificationSystemPrompt, setReportingClassificationSystemPrompt] =
    useState<string>('');
  const [reportingUseCustomTaxonomy, setReportingUseCustomTaxonomy] =
    useState<boolean>(false);
  const [reportingTaxonomy, setReportingTaxonomy] = useState<ClassificationTaxonomy>({
    questionTypes: [],
    tones: [],
    topicAreas: [],
  });

  // Metadata
  const [version, setVersion] = useState(0);
  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // ADMIN-V1-R14: Feature flag management state
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagInfo[]>([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [flagsError, setFlagsError] = useState<string | null>(null);
  const [togglingFlag, setTogglingFlag] = useState<string | null>(null);
  const [flagSuccess, setFlagSuccess] = useState<string | null>(null);
  // SAFETY-V1-R05: Safety configuration state (read-only)
  const [safetyConfig, setSafetyConfig] = useState<SafetyConfigData | null>(null);
  const [safetyLoading, setSafetyLoading] = useState(false);
  const [safetyError, setSafetyError] = useState<string | null>(null);
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  // Sync form state from branding context
  useEffect(() => {
    setAppName(branding.appName);
    setLogoUrl(branding.logoUrl ?? '');
    setLogoDarkUrl(branding.logoDarkUrl ?? '');
    setLogoAltText(branding.logoAltText);
    setFaviconUrl(branding.faviconUrl ?? '');
    setLightColors(branding.colors);
    setDarkColors(branding.darkColors);
    setDisclaimers(branding.disclaimers);
    setCitationDisplayMode(branding.citationDisplayMode);
    setChatSystemPrompt(branding.chatSystemPrompt ?? '');
    setChatUngroundedFallbackMessage(branding.chatUngroundedFallbackMessage ?? '');
    setGalenSearchSystemPrompt(branding.galenSearchSystemPrompt ?? '');
    setGalenSearchUngroundedFallbackMessage(branding.galenSearchUngroundedFallbackMessage ?? '');
    setChatTemperature(branding.chatTemperature != null ? String(branding.chatTemperature) : '');
    setChatMaxTokens(branding.chatMaxTokens != null ? String(branding.chatMaxTokens) : '');
    setChatTopP(branding.chatTopP != null ? String(branding.chatTopP) : '');
    setChatGroundingTopN(branding.chatGroundingTopN != null ? String(branding.chatGroundingTopN) : '');
    setChatSemanticConfiguration(branding.chatSemanticConfiguration ?? '');
    setChatScoringProfile(branding.chatScoringProfile ?? '');
    setChatPastMessagesIncluded(branding.chatPastMessagesIncluded != null ? String(branding.chatPastMessagesIncluded) : '');
    setGalenSearchTemperature(branding.galenSearchTemperature != null ? String(branding.galenSearchTemperature) : '');
    setGalenSearchMaxTokens(branding.galenSearchMaxTokens != null ? String(branding.galenSearchMaxTokens) : '');
    setGalenSearchTopP(branding.galenSearchTopP != null ? String(branding.galenSearchTopP) : '');
    setGalenSearchGroundingTopN(branding.galenSearchGroundingTopN != null ? String(branding.galenSearchGroundingTopN) : '');
    setSearchHybridTopN(branding.searchHybridTopN != null ? String(branding.searchHybridTopN) : '');
    setGalenSearchSemanticConfiguration(branding.galenSearchSemanticConfiguration ?? '');
    setGalenSearchScoringProfile(branding.galenSearchScoringProfile ?? '');
    setGalenSearchPastMessagesIncluded(branding.galenSearchPastMessagesIncluded != null ? String(branding.galenSearchPastMessagesIncluded) : '');
    setSearchSemanticTopN(branding.searchSemanticTopN != null ? String(branding.searchSemanticTopN) : '');
    setSearchSimpleTopN(branding.searchSimpleTopN != null ? String(branding.searchSimpleTopN) : '');
    setSearchDefaultMode(branding.searchDefaultMode ?? '');
    setSemanticSearchDefaultMode(branding.semanticSearchDefaultMode ?? '');
    setGalenSearchDefaultMode(branding.galenSearchDefaultMode ?? '');
    setSearchSemanticConfiguration(branding.searchSemanticConfiguration ?? '');
    setSearchVectorK(branding.searchVectorK != null ? String(branding.searchVectorK) : '');
    setSearchVectorExhaustive(branding.searchVectorExhaustive ?? false);
    setQueryEmbeddingCacheTtlMinutes(branding.queryEmbeddingCacheTtlMinutes != null ? String(branding.queryEmbeddingCacheTtlMinutes) : '');
    setSearchVectorProfile(branding.searchVectorProfile ?? '');
    setCorColorMappings(branding.corColorMappings);
    setLoeColorMappings(branding.loeColorMappings);
    // Bug #4306: chat_use_grounding three-state.
    if (branding.chatUseGrounding === null || branding.chatUseGrounding === undefined) {
      setChatUseGrounding('default');
    } else {
      setChatUseGrounding(branding.chatUseGrounding ? 'enabled' : 'disabled');
    }
    // Bug #4307: reporting classification prompt + taxonomy.
    setReportingClassificationSystemPrompt(
      branding.reportingClassificationSystemPrompt ?? '',
    );
    if (branding.reportingClassificationTaxonomy) {
      setReportingUseCustomTaxonomy(true);
      setReportingTaxonomy(branding.reportingClassificationTaxonomy);
    } else {
      setReportingUseCustomTaxonomy(false);
      setReportingTaxonomy({ questionTypes: [], tones: [], topicAreas: [] });
    }
  }, [branding]);

  // Fetch metadata on mount
  useEffect(() => {
    async function fetchMeta(): Promise<void> {
      try {
        const res = await api.get<BrandingConfigApiResponse>('/config/branding');
        setVersion(res.data.version);
        setUpdatedAt(res.data.updated_at);
        setUpdatedBy(res.data.updated_by);
      } catch {
        // Non-critical, metadata will just be empty
      }
    }
    void fetchMeta();
  }, []);

  // ADMIN-V1-R14: Fetch feature flags for admin management
  const fetchFeatureFlags = useCallback(async () => {
    setFlagsLoading(true);
    setFlagsError(null);
    try {
      const res = await api.get<{ flags: FeatureFlagInfo[] }>('/admin/feature-flags');
      setFeatureFlags(res.data.flags);
    } catch (err) {
      setFlagsError(getErrorMessage(err));
    } finally {
      setFlagsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  // SAFETY-V1-R05: Fetch safety configuration (read-only)
  const fetchSafetyConfig = useCallback(async () => {
    setSafetyLoading(true);
    setSafetyError(null);
    try {
      const res = await api.get<SafetyConfigData>('/admin/safety-ruleset');
      setSafetyConfig(res.data);
    } catch (err) {
      setSafetyError(getErrorMessage(err));
    } finally {
      setSafetyLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSafetyConfig();
  }, [fetchSafetyConfig]);

  const toggleRuleExpanded = useCallback((ruleId: string) => {
    setExpandedRules((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) {
        next.delete(ruleId);
      } else {
        next.add(ruleId);
      }
      return next;
    });
  }, []);

  // ADMIN-V1-R14: Toggle a feature flag
  const handleToggleFlag = useCallback(async (flagName: string, newState: boolean) => {
    setTogglingFlag(flagName);
    setFlagsError(null);
    try {
      await api.put(`/admin/feature-flags/${flagName}`, { enabled: newState });
      setFlagSuccess(`${flagName} ${newState ? 'enabled' : 'disabled'}`);
      setTimeout(() => setFlagSuccess(null), 3000);
      await fetchFeatureFlags();
      await featureFlagRefetch();
    } catch (err) {
      setFlagsError(getErrorMessage(err));
    } finally {
      setTogglingFlag(null);
    }
  }, [fetchFeatureFlags, featureFlagRefetch]);

  const handleSave = useCallback(async () => {
    // ADMIN-V1-R13 (Bug #5384): Refuse to save from a never-hydrated form.
    //
    // The sync effect above populates ~40 fields from the branding context. If
    // the branding fetch never succeeded, that context is still DEFAULT_BRANDING
    // (all-null), every text field renders empty showing only its placeholder,
    // and saving submits those empties as null -- wiping the real values in
    // client_branding. That is what happened in ESC production on 2026-07-15:
    // the custom chat_system_prompt, unchanged since 2026-03-25, was nulled and
    // chat silently fell back to the generic DEFAULT_SYSTEM_TEMPLATE.
    //
    // The auth-transition refetch in BrandingContext should prevent the
    // un-hydrated state arising at all. This guard is deliberate defence in
    // depth: the underlying fault is a race, so no single fix can be proven to
    // close every path into that state, and the failure mode here is silent
    // destruction of admin configuration.
    if (!brandingLoaded) {
      setSaveError(
        'Configuration has not finished loading, so saving now could overwrite ' +
          'your saved settings with blank values. Reload the page and try again.'
      );
      setSaveSuccess(false);
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const payload = {
        config: {
          app_name: appName,
          logo_url: logoUrl || null,
          logo_dark_url: logoDarkUrl || null,
          logo_alt_text: logoAltText,
          favicon_url: faviconUrl || null,
          colors: {
            background: lightColors.background || null,
            foreground: lightColors.foreground || null,
            primary: lightColors.primary || null,
            primary_foreground: lightColors.primaryForeground || null,
            secondary: lightColors.secondary || null,
            secondary_foreground: lightColors.secondaryForeground || null,
            accent: lightColors.accent || null,
            accent_foreground: lightColors.accentForeground || null,
          },
          dark_colors: {
            background: darkColors.background || null,
            foreground: darkColors.foreground || null,
            primary: darkColors.primary || null,
            primary_foreground: darkColors.primaryForeground || null,
            secondary: darkColors.secondary || null,
            secondary_foreground: darkColors.secondaryForeground || null,
            accent: darkColors.accent || null,
            accent_foreground: darkColors.accentForeground || null,
          },
          disclaimers: {
            safety_disclaimer: disclaimers.safetyDisclaimer || null,
            response_disclaimer: disclaimers.responseDisclaimer || null,
            footer_text: disclaimers.footerText || null,
            footer_regulatory: disclaimers.footerRegulatory || null,
          },
          citation_display_mode: citationDisplayMode,
          chat_system_prompt: chatSystemPrompt || null,
          chat_ungrounded_fallback_message: chatUngroundedFallbackMessage || null,
          galen_search_system_prompt: galenSearchSystemPrompt || null,
          galen_search_ungrounded_fallback_message: galenSearchUngroundedFallbackMessage || null,
          chat_temperature: chatTemperature ? parseFloat(chatTemperature) : null,
          chat_max_tokens: chatMaxTokens ? parseInt(chatMaxTokens, 10) : null,
          chat_top_p: chatTopP ? parseFloat(chatTopP) : null,
          chat_grounding_top_n: chatGroundingTopN ? parseInt(chatGroundingTopN, 10) : null,
          chat_semantic_configuration: chatSemanticConfiguration || null,
          chat_scoring_profile: chatScoringProfile || null,
          chat_past_messages_included: chatPastMessagesIncluded ? parseInt(chatPastMessagesIncluded, 10) : null,
          galen_search_temperature: galenSearchTemperature ? parseFloat(galenSearchTemperature) : null,
          galen_search_max_tokens: galenSearchMaxTokens ? parseInt(galenSearchMaxTokens, 10) : null,
          galen_search_top_p: galenSearchTopP ? parseFloat(galenSearchTopP) : null,
          galen_search_grounding_top_n: galenSearchGroundingTopN ? parseInt(galenSearchGroundingTopN, 10) : null,
          search_hybrid_top_n: searchHybridTopN ? parseInt(searchHybridTopN, 10) : null,
          galen_search_semantic_configuration: galenSearchSemanticConfiguration || null,
          galen_search_scoring_profile: galenSearchScoringProfile || null,
          galen_search_past_messages_included: galenSearchPastMessagesIncluded ? parseInt(galenSearchPastMessagesIncluded, 10) : null,
          search_semantic_top_n: searchSemanticTopN ? parseInt(searchSemanticTopN, 10) : null,
          search_simple_top_n: searchSimpleTopN ? parseInt(searchSimpleTopN, 10) : null,
          search_default_mode: searchDefaultMode || null,
          semantic_search_default_mode: semanticSearchDefaultMode || null,
          galen_search_default_mode: galenSearchDefaultMode || null,
          search_semantic_configuration: searchSemanticConfiguration || null,
          search_vector_k: searchVectorK ? parseInt(searchVectorK, 10) : null,
          search_vector_exhaustive: searchVectorExhaustive || null,
          query_embedding_cache_ttl_minutes: queryEmbeddingCacheTtlMinutes ? parseInt(queryEmbeddingCacheTtlMinutes, 10) : null,
          search_vector_profile: searchVectorProfile || null,
          cor_color_mappings: corColorMappings.filter((m) => m.value && m.color),
          loe_color_mappings: loeColorMappings.filter((m) => m.value && m.color),
          // Bug #4306: chat_use_grounding. Three-state UI maps to
          // boolean|null on the wire: 'default' means "use env var".
          chat_use_grounding:
            chatUseGrounding === 'default'
              ? null
              : chatUseGrounding === 'enabled',
          // Bug #4307: reporting classification prompt + taxonomy.
          reporting_classification_system_prompt:
            reportingClassificationSystemPrompt.trim() === ''
              ? null
              : reportingClassificationSystemPrompt,
          reporting_classification_taxonomy: reportingUseCustomTaxonomy
            ? {
                question_types: reportingTaxonomy.questionTypes
                  .map((q) => q.trim())
                  .filter((q) => q.length > 0),
                tones: reportingTaxonomy.tones
                  .map((t) => t.trim())
                  .filter((t) => t.length > 0),
                topic_areas: reportingTaxonomy.topicAreas
                  .map((a) => a.trim())
                  .filter((a) => a.length > 0),
              }
            : null,
        },
      };

      const res = await api.put<BrandingConfigApiResponse>('/config/branding', payload);
      setVersion(res.data.version);
      setUpdatedAt(res.data.updated_at);
      setUpdatedBy(res.data.updated_by);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // Refresh context to apply changes immediately
      await refetch();
    } catch (err) {
      setSaveError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  }, [appName, logoUrl, logoDarkUrl, logoAltText, faviconUrl, lightColors, darkColors, disclaimers, citationDisplayMode, chatSystemPrompt, chatUngroundedFallbackMessage, galenSearchSystemPrompt, galenSearchUngroundedFallbackMessage, chatTemperature, chatMaxTokens, chatTopP, chatGroundingTopN, chatSemanticConfiguration, chatScoringProfile, chatPastMessagesIncluded, galenSearchTemperature, galenSearchMaxTokens, galenSearchTopP, galenSearchGroundingTopN, searchHybridTopN, galenSearchSemanticConfiguration, galenSearchScoringProfile, galenSearchPastMessagesIncluded, searchSemanticTopN, searchSimpleTopN, searchDefaultMode, semanticSearchDefaultMode, galenSearchDefaultMode, searchSemanticConfiguration, searchVectorK, searchVectorExhaustive, queryEmbeddingCacheTtlMinutes, searchVectorProfile, corColorMappings, loeColorMappings, chatUseGrounding, reportingClassificationSystemPrompt, reportingUseCustomTaxonomy, reportingTaxonomy, refetch, brandingLoaded]);

  const handleReset = useCallback(async () => {
    setIsResetting(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const res = await api.post<BrandingConfigApiResponse>('/config/branding/reset');
      setVersion(res.data.version);
      setUpdatedAt(res.data.updated_at);
      setUpdatedBy(res.data.updated_by);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      await refetch();
    } catch (err) {
      setSaveError(getErrorMessage(err));
    } finally {
      setIsResetting(false);
    }
  }, [refetch]);

  const updateLightColor = useCallback((key: keyof BrandingColors, value: string) => {
    setLightColors((prev) => ({ ...prev, [key]: value || null }));
  }, []);

  const updateDarkColor = useCallback((key: keyof BrandingColors, value: string) => {
    setDarkColors((prev) => ({ ...prev, [key]: value || null }));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Configuration</h1>
          <p className="text-sm text-muted-foreground">
            Manage portal appearance, AI service parameters, and search settings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {version > 0 && (
            <Badge variant="outline">v{version}</Badge>
          )}
        </div>
      </div>

      {saveError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}

      {saveSuccess && (
        <Alert>
          <AlertDescription>Configuration saved successfully.</AlertDescription>
        </Alert>
      )}

      {/* Top-level tab navigation (CONFIG-V1-R17) */}
      <Tabs defaultValue="global">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="global">Global</TabsTrigger>
          <TabsTrigger value="galen-chat">Galen Chat</TabsTrigger>
          <TabsTrigger value="galen-search">Galen Search</TabsTrigger>
          <TabsTrigger value="search-settings">Search Settings</TabsTrigger>
          <TabsTrigger value="safety">Safety</TabsTrigger>
          <TabsTrigger value="reporting">Reporting</TabsTrigger>
        </TabsList>

        {/* ===== GLOBAL TAB ===== */}
        <TabsContent value="global" className="mt-6 space-y-6">
          {/* Identity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                Identity
              </CardTitle>
              <CardDescription>
                Application name, logo, and favicon settings.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="app-name">Application Name</Label>
                  <Input
                    id="app-name"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    placeholder="Galen Portal"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo-alt">Logo Alt Text</Label>
                  <Input
                    id="logo-alt"
                    value={logoAltText}
                    onChange={(e) => setLogoAltText(e.target.value)}
                    placeholder="Galen Portal"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo-url">Logo URL</Label>
                <Input
                  id="logo-url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.svg"
                />
                {logoUrl && (
                  <div className="mt-2 rounded border p-3 bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <img
                      src={logoUrl}
                      alt={logoAltText || 'Logo preview'}
                      className="h-10 max-w-[200px] object-contain"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo-dark-url">Dark Mode Logo URL</Label>
                <Input
                  id="logo-dark-url"
                  value={logoDarkUrl}
                  onChange={(e) => setLogoDarkUrl(e.target.value)}
                  placeholder="https://example.com/logo-dark.svg"
                />
                <p className="text-xs text-muted-foreground">
                  Used when dark mode is active. Falls back to the main logo if not set.
                </p>
                {logoDarkUrl && (
                  <div className="mt-2 rounded border p-3 bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <img
                      src={logoDarkUrl}
                      alt={logoAltText || 'Dark mode logo preview'}
                      className="h-10 max-w-[200px] object-contain"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="favicon-url">Favicon URL</Label>
                <Input
                  id="favicon-url"
                  value={faviconUrl}
                  onChange={(e) => setFaviconUrl(e.target.value)}
                  placeholder="https://example.com/favicon.ico"
                />
                {faviconUrl && (
                  <div className="mt-2 flex items-center gap-2">
                    <p className="text-xs text-muted-foreground">Preview:</p>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                    <img
                      src={faviconUrl}
                      alt="Favicon preview"
                      className="h-5 w-5 object-contain"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Colors */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Colors
              </CardTitle>
              <CardDescription>
                HSL color overrides for light and dark modes. Use format like &quot;222.2 47.4% 11.2%&quot;.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="light">
                <TabsList>
                  <TabsTrigger value="light">Light Mode</TabsTrigger>
                  <TabsTrigger value="dark">Dark Mode</TabsTrigger>
                </TabsList>

                <TabsContent value="light" className="mt-4">
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {COLOR_FIELDS.map((field) => (
                      <div key={field.key} className="space-y-2">
                        <Label htmlFor={`light-${field.key}`}>{field.label}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id={`light-${field.key}`}
                            value={lightColors[field.key] ?? ''}
                            onChange={(e) => updateLightColor(field.key, e.target.value)}
                            placeholder="222.2 47.4% 11.2%"
                          />
                          {lightColors[field.key] && (
                            <div
                              className="h-8 w-8 shrink-0 rounded border"
                              style={{ backgroundColor: hslToPreviewColor(lightColors[field.key]) }}
                              aria-label={`${field.label} color preview`}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="dark" className="mt-4">
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {COLOR_FIELDS.map((field) => (
                      <div key={field.key} className="space-y-2">
                        <Label htmlFor={`dark-${field.key}`}>{field.label}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id={`dark-${field.key}`}
                            value={darkColors[field.key] ?? ''}
                            onChange={(e) => updateDarkColor(field.key, e.target.value)}
                            placeholder="210 40% 98%"
                          />
                          {darkColors[field.key] && (
                            <div
                              className="h-8 w-8 shrink-0 rounded border"
                              style={{ backgroundColor: hslToPreviewColor(darkColors[field.key]) }}
                              aria-label={`${field.label} dark color preview`}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Disclaimers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Disclaimers
              </CardTitle>
              <CardDescription>
                Customizable legal and regulatory text. Leave blank to use CE-compliant defaults.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="safety-disclaimer">Safety Disclaimer (below chat input)</Label>
                <Textarea
                  id="safety-disclaimer"
                  value={disclaimers.safetyDisclaimer ?? ''}
                  onChange={(e) => setDisclaimers((prev) => ({ ...prev, safetyDisclaimer: e.target.value || null }))}
                  placeholder="Galen Chat retrieves content from published clinical guidelines. It does not analyse patient data, recommend diagnostic or therapeutic actions, or interpret clinical parameters. All clinical decisions remain the sole responsibility of the healthcare professional."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="response-disclaimer">Response Disclaimer (below each response)</Label>
                <Textarea
                  id="response-disclaimer"
                  value={disclaimers.responseDisclaimer ?? ''}
                  onChange={(e) => setDisclaimers((prev) => ({ ...prev, responseDisclaimer: e.target.value || null }))}
                  placeholder="This summarises guideline content relevant to the question and does not determine management for an individual patient."
                  rows={2}
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="footer-text">Footer Text</Label>
                <Input
                  id="footer-text"
                  value={disclaimers.footerText ?? ''}
                  onChange={(e) => setDisclaimers((prev) => ({ ...prev, footerText: e.target.value || null }))}
                  placeholder="Galen Portal - AI-Assisted Clinical Guideline Retrieval System"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="footer-regulatory">Footer Regulatory Text</Label>
                <Input
                  id="footer-regulatory"
                  value={disclaimers.footerRegulatory ?? ''}
                  onChange={(e) => setDisclaimers((prev) => ({ ...prev, footerRegulatory: e.target.value || null }))}
                  placeholder="EU MDR Class I Medical Device"
                />
              </div>
            </CardContent>
          </Card>

          {/* Search Result Colour Indicators (SEARCH-SEMANTIC-V1-R03) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Search Result Colour Indicators
              </CardTitle>
              <CardDescription>
                Configure COR (Class of Recommendation) and LOE (Level of Evidence) colour bars
                displayed on semantic search result cards. Add string-to-colour mappings for each
                value that appears in your search index.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* COR Colour Mappings */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>COR (Class of Recommendation) Colours</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCorColorMappings((prev) => [...prev, { value: '', color: '#22c55e' }])}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add
                  </Button>
                </div>
                {corColorMappings.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No COR colour mappings configured. Add mappings like &quot;I&quot; = green, &quot;IIa&quot; = yellow, etc.
                  </p>
                )}
                {corColorMappings.map((mapping, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={mapping.value}
                      onChange={(e) => {
                        setCorColorMappings((prev) =>
                          prev.map((m, i) => i === idx ? { value: e.target.value, color: m.color } : m)
                        );
                      }}
                      placeholder="e.g. I, IIa, IIb, III"
                      className="w-32"
                      aria-label={`COR value ${idx + 1}`}
                    />
                    <Input
                      type="color"
                      value={mapping.color}
                      onChange={(e) => {
                        setCorColorMappings((prev) =>
                          prev.map((m, i) => i === idx ? { value: m.value, color: e.target.value } : m)
                        );
                      }}
                      className="h-9 w-14 cursor-pointer p-1"
                      aria-label={`COR colour ${idx + 1}`}
                    />
                    <span className="text-xs text-muted-foreground w-20 truncate">{mapping.color}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setCorColorMappings((prev) => prev.filter((_, i) => i !== idx))}
                      aria-label={`Remove COR mapping ${idx + 1}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>

              <Separator />

              {/* LOE Colour Mappings */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>LOE (Level of Evidence) Colours</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setLoeColorMappings((prev) => [...prev, { value: '', color: '#3b82f6' }])}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add
                  </Button>
                </div>
                {loeColorMappings.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No LOE colour mappings configured. Add mappings like &quot;A&quot; = blue, &quot;B&quot; = orange, etc.
                  </p>
                )}
                {loeColorMappings.map((mapping, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={mapping.value}
                      onChange={(e) => {
                        setLoeColorMappings((prev) =>
                          prev.map((m, i) => i === idx ? { value: e.target.value, color: m.color } : m)
                        );
                      }}
                      placeholder="e.g. A, B, C"
                      className="w-32"
                      aria-label={`LOE value ${idx + 1}`}
                    />
                    <Input
                      type="color"
                      value={mapping.color}
                      onChange={(e) => {
                        setLoeColorMappings((prev) =>
                          prev.map((m, i) => i === idx ? { value: m.value, color: e.target.value } : m)
                        );
                      }}
                      className="h-9 w-14 cursor-pointer p-1"
                      aria-label={`LOE colour ${idx + 1}`}
                    />
                    <span className="text-xs text-muted-foreground w-20 truncate">{mapping.color}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setLoeColorMappings((prev) => prev.filter((_, i) => i !== idx))}
                      aria-label={`Remove LOE mapping ${idx + 1}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ADMIN-V1-R14: Feature Flags Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ToggleRight className="h-5 w-5" />
                Feature Flags
              </CardTitle>
              <CardDescription>
                Enable or disable platform features. Changes take effect immediately.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {flagsLoading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading feature flags...
                </div>
              )}
              {flagsError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{flagsError}</AlertDescription>
                </Alert>
              )}
              {flagSuccess && (
                <Alert>
                  <AlertDescription>{flagSuccess}</AlertDescription>
                </Alert>
              )}
              {featureFlags.map((flag) => (
                <div key={flag.name} className="flex items-center justify-between rounded-md border p-3">
                  <div className="space-y-1">
                    <Label>{flag.name}</Label>
                    <p className="text-xs text-muted-foreground">{flag.description}</p>
                    {flag.dependencies.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        Requires: {flag.dependencies.join(', ')}
                      </Badge>
                    )}
                  </div>
                  <Switch
                    checked={flag.enabled}
                    onCheckedChange={(checked) => { void handleToggleFlag(flag.name, checked); }}
                    disabled={togglingFlag === flag.name}
                    aria-label={`Toggle ${flag.name}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== GALEN CHAT TAB ===== */}
        <TabsContent value="galen-chat" className="mt-6 space-y-6">
          {/* Chat Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Chat Settings
              </CardTitle>
              <CardDescription>
                Configure citation display and prompts for Galen Chat.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="citation-display-mode">Citation Detail Display Mode</Label>
                <Select
                  value={citationDisplayMode}
                  onValueChange={(value: CitationDisplayMode) => setCitationDisplayMode(value)}
                >
                  <SelectTrigger id="citation-display-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inline">Inline (expand within chat)</SelectItem>
                    <SelectItem value="side_panel">Side Panel (open beside chat)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Controls how citation details are shown when a user clicks a reference.
                  &quot;Inline&quot; expands content within the chat flow. &quot;Side Panel&quot; opens a panel to the right of the chat.
                </p>
              </div>

              <Separator />

              {/* CONFIG-V1-R17: Chat Prompts */}
              <div className="space-y-2">
                <Label htmlFor="chat-system-prompt">System Prompt</Label>
                <Textarea
                  id="chat-system-prompt"
                  value={chatSystemPrompt}
                  onChange={(e) => setChatSystemPrompt(e.target.value)}
                  placeholder="You are a clinical guidelines assistant specialising in European Society of Cardiology (ESC) guidelines. Your role is to help healthcare professionals find and understand recommendations from ESC guidelines."
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  Custom system prompt for the Galen Chat LLM. Leave blank to use the default clinical guidelines prompt.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="chat-fallback-message">Ungrounded Fallback Message</Label>
                <Textarea
                  id="chat-fallback-message"
                  value={chatUngroundedFallbackMessage}
                  onChange={(e) => setChatUngroundedFallbackMessage(e.target.value)}
                  placeholder="I could not find specific ESC guideline content directly relevant to your question. The following search results may be related:"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Message displayed when the LLM response contains no citation references. Leave blank to use the default.
                </p>
              </div>

              <Separator />

              {/* CONFIG-V1-R11 (Bug #4306): chat_use_grounding three-state select. */}
              <div className="space-y-2">
                <Label htmlFor="chat-use-grounding">Search Grounding</Label>
                <Select
                  value={chatUseGrounding}
                  onValueChange={(value: 'default' | 'enabled' | 'disabled') =>
                    setChatUseGrounding(value)
                  }
                >
                  <SelectTrigger id="chat-use-grounding">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Use deployment default</SelectItem>
                    <SelectItem value="enabled">Enabled (ground responses with search results)</SelectItem>
                    <SelectItem value="disabled">Disabled (respond from LLM only)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Controls whether Galen Chat retrieves and grounds its responses in indexed guideline content.
                  &quot;Use deployment default&quot; falls back to the <code>CHAT_USE_GROUNDING</code> environment variable
                  (typically enabled).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Chat LLM Settings (CONFIG-V1-R17, Bug #3888) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cpu className="h-5 w-5" />
                Chat LLM Settings
              </CardTitle>
              <CardDescription>
                Configure LLM behaviour parameters specific to Galen Chat. Leave fields blank to use
                environment variable defaults.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="chat-temperature">Temperature</Label>
                  <Input
                    id="chat-temperature"
                    type="number"
                    min={0}
                    max={2}
                    step={0.1}
                    value={chatTemperature}
                    onChange={(e) => setChatTemperature(e.target.value)}
                    placeholder="0.7"
                  />
                  <p className="text-xs text-muted-foreground">0.0 &ndash; 2.0</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chat-max-tokens">Max Tokens</Label>
                  <Input
                    id="chat-max-tokens"
                    type="number"
                    min={1}
                    max={8000}
                    step={1}
                    value={chatMaxTokens}
                    onChange={(e) => setChatMaxTokens(e.target.value)}
                    placeholder="2000"
                  />
                  <p className="text-xs text-muted-foreground">1 &ndash; 8000</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chat-top-p">Top P</Label>
                  <Input
                    id="chat-top-p"
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={chatTopP}
                    onChange={(e) => setChatTopP(e.target.value)}
                    placeholder="1.0"
                  />
                  <p className="text-xs text-muted-foreground">0.0 &ndash; 1.0</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chat-grounding-top-n">Grounding Top N</Label>
                  <Input
                    id="chat-grounding-top-n"
                    type="number"
                    min={1}
                    max={20}
                    step={1}
                    value={chatGroundingTopN}
                    onChange={(e) => setChatGroundingTopN(e.target.value)}
                    placeholder="5"
                  />
                  <p className="text-xs text-muted-foreground">Search results sent to LLM (1 &ndash; 20)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chat-past-messages">Past Messages Included</Label>
                  <Input
                    id="chat-past-messages"
                    type="number"
                    min={1}
                    max={50}
                    step={1}
                    value={chatPastMessagesIncluded}
                    onChange={(e) => setChatPastMessagesIncluded(e.target.value)}
                    placeholder="10"
                  />
                  <p className="text-xs text-muted-foreground">Conversation turns sent to LLM (1 &ndash; 50)</p>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="chat-semantic-config">Semantic Configuration</Label>
                  <Input
                    id="chat-semantic-config"
                    value={chatSemanticConfiguration}
                    onChange={(e) => setChatSemanticConfiguration(e.target.value)}
                    placeholder="semantic-config"
                  />
                  <p className="text-xs text-muted-foreground">
                    Azure Search semantic configuration name for Chat grounding.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="chat-scoring-profile">Scoring Profile</Label>
                  <Input
                    id="chat-scoring-profile"
                    value={chatScoringProfile}
                    onChange={(e) => setChatScoringProfile(e.target.value)}
                    placeholder=""
                  />
                  <p className="text-xs text-muted-foreground">
                    Azure Search scoring profile for Chat grounding. Leave blank for default.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== GALEN SEARCH TAB ===== */}
        <TabsContent value="galen-search" className="mt-6 space-y-6">
          {/* Galen Search Settings (CONFIG-V1-R17, Bug #3887) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Galen Search Prompts
              </CardTitle>
              <CardDescription>
                Configure the system prompt and fallback message for Galen Search (combined search + LLM).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="galen-search-system-prompt">System Prompt</Label>
                <Textarea
                  id="galen-search-system-prompt"
                  value={galenSearchSystemPrompt}
                  onChange={(e) => setGalenSearchSystemPrompt(e.target.value)}
                  placeholder="You are a clinical guidelines assistant specialising in European Society of Cardiology (ESC) guidelines. Your role is to help healthcare professionals find and understand recommendations from ESC guidelines."
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  Custom system prompt for the Galen Search LLM. Leave blank to use the default clinical guidelines prompt.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="galen-search-fallback-message">Ungrounded Fallback Message</Label>
                <Textarea
                  id="galen-search-fallback-message"
                  value={galenSearchUngroundedFallbackMessage}
                  onChange={(e) => setGalenSearchUngroundedFallbackMessage(e.target.value)}
                  placeholder="I could not find specific ESC guideline content directly relevant to your search. The following search results may be related:"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Message displayed when the Galen Search LLM response contains no citation references. Leave blank to use the default.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Galen Search LLM Settings (CONFIG-V1-R17, Bug #3889) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cpu className="h-5 w-5" />
                Galen Search LLM Settings
              </CardTitle>
              <CardDescription>
                Configure LLM behaviour parameters specific to Galen Search. Leave fields blank to use
                environment variable defaults.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="search-temperature">Temperature</Label>
                  <Input
                    id="search-temperature"
                    type="number"
                    min={0}
                    max={2}
                    step={0.1}
                    value={galenSearchTemperature}
                    onChange={(e) => setGalenSearchTemperature(e.target.value)}
                    placeholder="0.7"
                  />
                  <p className="text-xs text-muted-foreground">0.0 &ndash; 2.0</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="search-max-tokens">Max Tokens</Label>
                  <Input
                    id="search-max-tokens"
                    type="number"
                    min={1}
                    max={8000}
                    step={1}
                    value={galenSearchMaxTokens}
                    onChange={(e) => setGalenSearchMaxTokens(e.target.value)}
                    placeholder="2000"
                  />
                  <p className="text-xs text-muted-foreground">1 &ndash; 8000</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="search-top-p">Top P</Label>
                  <Input
                    id="search-top-p"
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={galenSearchTopP}
                    onChange={(e) => setGalenSearchTopP(e.target.value)}
                    placeholder="1.0"
                  />
                  <p className="text-xs text-muted-foreground">0.0 &ndash; 1.0</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="search-grounding-top-n">Grounding Top N</Label>
                  <Input
                    id="search-grounding-top-n"
                    type="number"
                    min={1}
                    max={20}
                    step={1}
                    value={galenSearchGroundingTopN}
                    onChange={(e) => setGalenSearchGroundingTopN(e.target.value)}
                    placeholder="5"
                  />
                  <p className="text-xs text-muted-foreground">Results sent to LLM (1 &ndash; 20)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="search-hybrid-top-n">Display Result Count</Label>
                  <Input
                    id="search-hybrid-top-n"
                    type="number"
                    min={1}
                    max={100}
                    step={1}
                    value={searchHybridTopN}
                    onChange={(e) => setSearchHybridTopN(e.target.value)}
                    placeholder="5"
                  />
                  <p className="text-xs text-muted-foreground">Results shown on page (1 &ndash; 100)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="search-past-messages">Past Messages Included</Label>
                  <Input
                    id="search-past-messages"
                    type="number"
                    min={0}
                    max={10}
                    step={1}
                    value={galenSearchPastMessagesIncluded}
                    onChange={(e) => setGalenSearchPastMessagesIncluded(e.target.value)}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground">Conversation turns (0 = none, 0 &ndash; 10)</p>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="search-semantic-config">Semantic Configuration</Label>
                  <Input
                    id="search-semantic-config"
                    value={galenSearchSemanticConfiguration}
                    onChange={(e) => setGalenSearchSemanticConfiguration(e.target.value)}
                    placeholder="semantic-config"
                  />
                  <p className="text-xs text-muted-foreground">
                    Azure Search semantic configuration name for Search grounding.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="search-scoring-profile">Scoring Profile</Label>
                  <Input
                    id="search-scoring-profile"
                    value={galenSearchScoringProfile}
                    onChange={(e) => setGalenSearchScoringProfile(e.target.value)}
                    placeholder=""
                  />
                  <p className="text-xs text-muted-foreground">
                    Azure Search scoring profile for Search grounding. Leave blank for default.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== SEARCH SETTINGS TAB ===== */}
        <TabsContent value="search-settings" className="mt-6 space-y-6">
          {/* Search Result Counts (CONFIG-V1-R17, Bug #3890) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search Page Result Counts
              </CardTitle>
              <CardDescription>
                Configure the default number of results shown on the Semantic Search and Simple Search pages.
                Leave blank to use environment variable defaults.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="search-semantic-top-n">Semantic Search Result Count</Label>
                  <Input
                    id="search-semantic-top-n"
                    type="number"
                    min={1}
                    max={100}
                    step={1}
                    value={searchSemanticTopN}
                    onChange={(e) => setSearchSemanticTopN(e.target.value)}
                    placeholder="10"
                  />
                  <p className="text-xs text-muted-foreground">Results per page on Semantic Search (1 &ndash; 100)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="search-simple-top-n">Simple Search Result Count</Label>
                  <Input
                    id="search-simple-top-n"
                    type="number"
                    min={1}
                    max={100}
                    step={1}
                    value={searchSimpleTopN}
                    onChange={(e) => setSearchSimpleTopN(e.target.value)}
                    placeholder="10"
                  />
                  <p className="text-xs text-muted-foreground">Results per page on Simple Search (1 &ndash; 100)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Search Defaults (CONFIG-V1-R17, Bug #3890, Bug #3979) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Search Defaults
              </CardTitle>
              <CardDescription>
                Configure the default search mode for each search page and the Azure Search semantic configuration.
                Non-admin users will always use the mode configured here.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Bug #3979: Separate search mode setting for Semantic Search page */}
                <div className="space-y-2">
                  <Label htmlFor="semantic-search-default-mode">Semantic Search Mode</Label>
                  <Select
                    value={semanticSearchDefaultMode || ''}
                    onValueChange={(value: string) => setSemanticSearchDefaultMode(value)}
                  >
                    <SelectTrigger id="semantic-search-default-mode">
                      <SelectValue placeholder="Default (hybrid)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hybrid">Hybrid (keyword + semantic)</SelectItem>
                      <SelectItem value="semantic">Semantic only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Search mode used on the Semantic Search page.
                  </p>
                </div>
                {/* Bug #3979: Separate search mode setting for Galen Search page */}
                <div className="space-y-2">
                  <Label htmlFor="galen-search-default-mode">Galen Search Mode</Label>
                  <Select
                    value={galenSearchDefaultMode || ''}
                    onValueChange={(value: string) => setGalenSearchDefaultMode(value)}
                  >
                    <SelectTrigger id="galen-search-default-mode">
                      <SelectValue placeholder="Default (hybrid)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto</SelectItem>
                      <SelectItem value="hybrid">Hybrid (keyword + semantic)</SelectItem>
                      <SelectItem value="semantic">Semantic only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Search mode used on the Galen Search page.
                  </p>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="search-semantic-config">Semantic Configuration</Label>
                  <Input
                    id="search-semantic-config"
                    value={searchSemanticConfiguration}
                    onChange={(e) => setSearchSemanticConfiguration(e.target.value)}
                    placeholder="semantic-config"
                  />
                  <p className="text-xs text-muted-foreground">
                    Azure Search semantic configuration name for Semantic/Simple Search pages.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vector Search Settings (Bug #3893) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Vector Search Settings
              </CardTitle>
              <CardDescription>
                Configure vector search parameters for true hybrid search (BM25 + vector + semantic reranker).
                Leave fields blank to use environment variable defaults.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="search-vector-k">Vector K</Label>
                  <Input
                    id="search-vector-k"
                    type="number"
                    min={1}
                    max={200}
                    step={1}
                    value={searchVectorK}
                    onChange={(e) => setSearchVectorK(e.target.value)}
                    placeholder="50"
                  />
                  <p className="text-xs text-muted-foreground">K nearest neighbors for vector search (1 &ndash; 200)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="embedding-cache-ttl">Embedding Cache TTL (minutes)</Label>
                  <Input
                    id="embedding-cache-ttl"
                    type="number"
                    min={0}
                    max={1440}
                    step={1}
                    value={queryEmbeddingCacheTtlMinutes}
                    onChange={(e) => setQueryEmbeddingCacheTtlMinutes(e.target.value)}
                    placeholder="60"
                  />
                  <p className="text-xs text-muted-foreground">0 disables caching (0 &ndash; 1440)</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vector-profile">Vector Profile Name</Label>
                  <Input
                    id="vector-profile"
                    value={searchVectorProfile}
                    onChange={(e) => setSearchVectorProfile(e.target.value)}
                    placeholder="myHnswProfile"
                  />
                  <p className="text-xs text-muted-foreground">Azure Search HNSW vector profile name</p>
                </div>
              </div>

              <Separator />

              <div className="flex items-center gap-3">
                <input
                  id="vector-exhaustive"
                  type="checkbox"
                  checked={searchVectorExhaustive}
                  onChange={(e) => setSearchVectorExhaustive(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <div>
                  <Label htmlFor="vector-exhaustive">Exhaustive KNN</Label>
                  <p className="text-xs text-muted-foreground">
                    Use exhaustive K-nearest neighbors instead of HNSW approximation.
                    More accurate but slower for large indexes.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== SAFETY TAB (SAFETY-V1-R05) ===== */}
        <TabsContent value="safety" className="mt-6 space-y-6">
          {/* Safety Ruleset Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Safety Ruleset
              </CardTitle>
              <CardDescription>
                Read-only view of the current safety validation ruleset. Safety rules are version-controlled
                and changes require editing safety_rules.json, committing, and redeploying (EU MDR compliance).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {safetyLoading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading safety configuration...
                </div>
              )}
              {safetyError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{safetyError}</AlertDescription>
                </Alert>
              )}
              {safetyConfig && (
                <>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-1">
                      <Label>Ruleset Version</Label>
                      <p className="text-sm font-medium">{safetyConfig.version}</p>
                    </div>
                    <div className="space-y-1">
                      <Label>Last Updated</Label>
                      <p className="text-sm font-medium">{safetyConfig.last_updated ?? 'Unknown'}</p>
                    </div>
                    <div className="space-y-1">
                      <Label>Total Rules</Label>
                      <p className="text-sm font-medium">{safetyConfig.rule_count}</p>
                    </div>
                  </div>

                  <Separator />

                  {/* Rules Table */}
                  <div>
                    <Label className="mb-2 block">Rules</Label>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Rule ID</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Response Message</TableHead>
                          <TableHead className="text-right">Patterns</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(safetyConfig.rules ?? []).map((rule) => (
                          <Collapsible key={rule.rule_id} asChild open={expandedRules.has(rule.rule_id)} onOpenChange={() => toggleRuleExpanded(rule.rule_id)}>
                            <>
                              <CollapsibleTrigger asChild>
                                <TableRow className="cursor-pointer">
                                  <TableCell>
                                    {expandedRules.has(rule.rule_id) ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">{rule.rule_id}</TableCell>
                                  <TableCell>
                                    {rule.effective_status === 'disabled' ? (
                                      <span title={rule.effective_status_reason ?? ''}>
                                        <Badge variant="outline" className="border-yellow-500 bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-400">Disabled</Badge>
                                      </span>
                                    ) : (
                                      <Badge variant="outline" className="border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400">Active</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{rule.category}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={rule.action === 'block' ? 'destructive' : 'secondary'}>
                                      {rule.action}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="max-w-xs truncate text-xs">{rule.message}</TableCell>
                                  <TableCell className="text-right">{rule.pattern_count}</TableCell>
                                </TableRow>
                              </CollapsibleTrigger>
                              <CollapsibleContent asChild>
                                <TableRow className="bg-muted/30">
                                  <TableCell colSpan={7} className="py-3">
                                    <div className="space-y-2 pl-6">
                                      {rule.effective_status === 'disabled' && rule.effective_status_reason && (
                                        <>
                                          <Label className="text-xs font-semibold">Disabled Reason</Label>
                                          <p className="text-xs text-yellow-600 dark:text-yellow-400">{rule.effective_status_reason}</p>
                                        </>
                                      )}
                                      <Label className="text-xs font-semibold">Regex Patterns</Label>
                                      <div className="space-y-1">
                                        {rule.patterns.map((pattern, idx) => (
                                          <code key={idx} className="block rounded bg-muted px-2 py-1 font-mono text-xs">
                                            {pattern}
                                          </code>
                                        ))}
                                      </div>
                                      <Label className="text-xs font-semibold">Full Response Message</Label>
                                      <p className="text-xs text-muted-foreground">{rule.message}</p>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              </CollapsibleContent>
                            </>
                          </Collapsible>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <Separator />

                  {/* Allowlist Context Patterns */}
                  <div>
                    <Label className="mb-2 block">Allowlist Context Patterns</Label>
                    <CardDescription className="mb-3">
                      Queries matching these context patterns may bypass safety blocking when combined with guideline-related intent.
                    </CardDescription>
                    <div className="flex flex-wrap gap-2">
                      {(safetyConfig.allowlist_context ?? []).map((pattern, idx) => (
                        <code key={idx} className="rounded bg-muted px-2 py-1 font-mono text-xs">
                          {pattern}
                        </code>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== REPORTING TAB (Bug #4307) ===== */}
        <TabsContent value="reporting" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Classification Prompt
              </CardTitle>
              <CardDescription>
                Custom system prompt for the REPORTING-V1 LLM turn-classification
                service. Leave blank to use the environment variable default
                (<code>REPORTING_CLASSIFICATION_SYSTEM_PROMPT</code>); blank that too
                and the built-in ESC cardiovascular prompt applies.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="reporting-classification-prompt">System Prompt</Label>
              <Textarea
                id="reporting-classification-prompt"
                value={reportingClassificationSystemPrompt}
                onChange={(e) =>
                  setReportingClassificationSystemPrompt(e.target.value)
                }
                placeholder="You are a clinical content classifier. Categorise each question by type, tone, and topic area drawn from the supplied taxonomy…"
                rows={6}
              />
              <p className="text-xs text-muted-foreground">
                Changes apply on the next classification run — either the nightly
                scheduler (if <code>ENABLE_REPORTING_SCHEDULER=true</code>) or a
                manual <code>POST /v1/reporting/classify</code> call by an admin.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Classification Taxonomy
              </CardTitle>
              <CardDescription>
                Replace the three taxonomy vocabularies the classifier draws
                from. Useful for non-ESC deployments (oncology, pulmonology, etc.)
                that need a domain-specific category set. Each list must contain at
                least one non-empty entry — the backend rejects empty lists.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="reporting-use-custom-taxonomy" className="cursor-pointer">
                  <input
                    type="checkbox"
                    id="reporting-use-custom-taxonomy"
                    checked={reportingUseCustomTaxonomy}
                    onChange={(e) => setReportingUseCustomTaxonomy(e.target.checked)}
                    className="mr-2 h-4 w-4"
                  />
                  Override the default taxonomy
                </Label>
              </div>
              {!reportingUseCustomTaxonomy && (
                <p className="text-xs text-muted-foreground">
                  Using the built-in ESC cardiovascular taxonomy
                  (<code>DEFAULT_CLASSIFICATION_TAXONOMY</code> in{' '}
                  <code>services/reporting/classification.py</code>).
                </p>
              )}
              {reportingUseCustomTaxonomy && (
                <div className="space-y-6">
                  <TaxonomyListEditor
                    id="taxonomy-question-types"
                    label="Question Types"
                    description="Categories the classifier selects from (e.g. treatment/management, differential-diagnosis). At least one entry required."
                    items={reportingTaxonomy.questionTypes}
                    onChange={(next) =>
                      setReportingTaxonomy({ ...reportingTaxonomy, questionTypes: next })
                    }
                  />
                  <TaxonomyListEditor
                    id="taxonomy-tones"
                    label="Tones"
                    description="Question-tone labels (e.g. quick-reference, urgent/time-sensitive). At least one entry required."
                    items={reportingTaxonomy.tones}
                    onChange={(next) =>
                      setReportingTaxonomy({ ...reportingTaxonomy, tones: next })
                    }
                  />
                  <TaxonomyListEditor
                    id="taxonomy-topic-areas"
                    label="Topic Areas"
                    description="Domain-specific topic tags (e.g. Heart Failure, Coronary Artery Disease for ESC; Breast Cancer, Leukemia for oncology). At least one entry required."
                    items={reportingTaxonomy.topicAreas}
                    onChange={(next) =>
                      setReportingTaxonomy({ ...reportingTaxonomy, topicAreas: next })
                    }
                  />
                  {!isTaxonomyValid(reportingTaxonomy) && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Every taxonomy list must contain at least one non-empty entry.
                        Save will fail server-side until all three lists are populated.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Actions — shared across all tabs (page-level) */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              {/*
                ADMIN-V1-R13 (Bug #5384): disabled until branding has loaded
                successfully at least once, so an un-hydrated form cannot be
                saved over the stored configuration. handleSave() re-checks
                the same condition -- the button state is the visible
                affordance, the guard in the handler is the actual protection.
              */}
              <Button
                onClick={() => void handleSave()}
                disabled={isSaving || isResetting || !brandingLoaded}
                title={
                  !brandingLoaded
                    ? 'Waiting for configuration to load. Saving is disabled until it does, to avoid overwriting saved settings with blank values.'
                    : undefined
                }
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Configuration
              </Button>
              <Button
                variant="outline"
                onClick={() => void handleReset()}
                disabled={isSaving || isResetting}
              >
                {isResetting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="mr-2 h-4 w-4" />
                )}
                Reset to Defaults
              </Button>
            </div>
            {(updatedAt || updatedBy) && (
              <div className="text-xs text-muted-foreground">
                {updatedBy && <span>Last updated by {updatedBy}</span>}
                {updatedAt && (
                  <span>
                    {updatedBy ? ' on ' : 'Last updated '}
                    {new Date(updatedAt).toLocaleString()}
                  </span>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Bug #4307: Validate a ClassificationTaxonomy client-side. Every list must
 * contain at least one non-empty, non-whitespace entry (mirrors the backend
 * Pydantic ``_no_empty_strings`` validator + ``min_length=1`` constraint).
 */
function isTaxonomyValid(taxonomy: ClassificationTaxonomy): boolean {
  const hasValidEntry = (list: string[]): boolean =>
    list.some((item) => item.trim().length > 0);
  return (
    hasValidEntry(taxonomy.questionTypes) &&
    hasValidEntry(taxonomy.tones) &&
    hasValidEntry(taxonomy.topicAreas)
  );
}

interface TaxonomyListEditorProps {
  id: string;
  label: string;
  description: string;
  items: string[];
  onChange: (next: string[]) => void;
}

/**
 * Bug #4307: Small list-editor for a single taxonomy vocabulary. One row
 * per entry with a remove button, plus an "Add entry" action. No drag-drop
 * reordering in this iteration — the classifier treats the list as a set,
 * not an ordered sequence, so order does not matter semantically.
 */
function TaxonomyListEditor({
  id,
  label,
  description,
  items,
  onChange,
}: TaxonomyListEditorProps): React.ReactElement {
  const handleItemChange = (index: number, value: string): void => {
    onChange(items.map((item, i) => (i === index ? value : item)));
  };
  const handleRemove = (index: number): void => {
    onChange(items.filter((_, i) => i !== index));
  };
  const handleAdd = (): void => {
    onChange([...items, '']);
  };
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <p className="text-xs text-muted-foreground">{description}</p>
      {items.length === 0 && (
        <p className="text-xs italic text-muted-foreground">No entries — click &quot;Add entry&quot; below.</p>
      )}
      <div className="space-y-2" id={id}>
        {items.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              aria-label={`${label} entry ${index + 1}`}
              value={item}
              onChange={(e) => handleItemChange(index, e.target.value)}
              placeholder="…"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Remove ${label.toLowerCase()} entry ${index + 1}`}
              onClick={() => handleRemove(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
        <Plus className="h-4 w-4 mr-2" />
        Add entry
      </Button>
    </div>
  );
}
